import { Capacitor } from '@capacitor/core';
import { API } from 'aws-amplify';
import axiosInstance from '@/config/axios.config';

/**
 * Unified API Service that handles both AWS Amplify and regular HTTP requests
 * 
 * IMPORTANT: On native platforms, we let AWS Amplify and axios handle the requests
 * as they should work properly in Capacitor's WebView with the correct network
 * security configuration.
 */
class ApiService {
  constructor() {
    this.isNative = Capacitor.isNativePlatform();
    this.platform = Capacitor.getPlatform();
    this.retryCount = 3;
    this.retryDelay = 1000;
    this.isDev = process.env.NODE_ENV === 'development';

    // Log platform detection (development only)
    if (this.isDev) {
      console.log('[API Service] Initialized:', JSON.stringify({
        isNative: this.isNative,
        platform: this.platform,
        userAgent: navigator.userAgent,
        apiConfig: API._options,
        amplifyVersion: API._config?.version
      }, null, 2));
    }
  }

  /**
   * Execute request with retry logic
   */
  async executeWithRetry(requestFn, retries = this.retryCount) {
    try {
      const result = await requestFn();
      if (this.isDev) console.log('[API Service] Request successful');
      return result;
    } catch (error) {
      if (this.isDev) {
        console.error('[API Service] Request failed:', JSON.stringify({
          message: error.message || 'Unknown error',
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          code: error.code,
          retries: retries
        }, null, 2));
      }

      if (retries > 0 && this.shouldRetry(error)) {
        if (this.isDev) console.log(`[API Service] Retrying... (${retries} retries left)`);
        await new Promise(resolve => setTimeout(resolve, this.retryDelay));
        return this.executeWithRetry(requestFn, retries - 1);
      }
      throw error;
    }
  }

  /**
   * Determine if request should be retried
   */
  shouldRetry(error) {
    // Don't retry on auth errors
    if (error.response?.status === 401 || error.response?.status === 403) {
      return false;
    }
    
    // Retry on network errors or 5xx server errors
    if (error.message?.includes('Network') || 
        error.code === 'NetworkError' || 
        error.code === 'ERR_NETWORK') {
      return true;
    }
    
    if (error.response?.status >= 500 && error.response?.status < 600) {
      return true;
    }
    
    // Retry on timeout
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return true;
    }
    
    return false;
  }

  /**
   * AWS Amplify API wrapper methods
   */
  async post(apiName, path, init) {
    if (this.isDev) console.log('[API Service] POST request:', { apiName, path, platform: this.platform });
    
    // Ensure init object has proper structure
    const requestInit = {
      ...init,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        ...(init?.headers || {})
      }
    };
    
    return await this.executeWithRetry(() => API.post(apiName, path, requestInit));
  }

  async get(apiName, path, init) {
    if (this.isDev) console.log('[API Service] GET request:', { apiName, path, platform: this.platform });
    
    // Ensure init object has proper structure
    const requestInit = {
      ...init,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        ...(init?.headers || {})
      }
    };
    
    return await this.executeWithRetry(() => API.get(apiName, path, requestInit));
  }

  async put(apiName, path, init) {
    if (this.isDev) console.log('[API Service] PUT request:', { apiName, path, platform: this.platform });

    // Ensure init object has proper structure
    const requestInit = {
      ...init,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        ...(init?.headers || {})
      }
    };

    return await this.executeWithRetry(() => API.put(apiName, path, requestInit));
  }

  async patch(apiName, path, init) {
    if (this.isDev) console.log('[API Service] PATCH request:', { apiName, path, platform: this.platform });

    // Ensure init object has proper structure
    const requestInit = {
      ...init,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        ...(init?.headers || {})
      }
    };

    return await this.executeWithRetry(() => API.patch(apiName, path, requestInit));
  }

  async del(apiName, path, init) {
    if (this.isDev) console.log('[API Service] DELETE request:', { apiName, path, platform: this.platform });
    
    // Ensure init object has proper structure
    const requestInit = {
      ...init,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        ...(init?.headers || {})
      }
    };
    
    return await this.executeWithRetry(() => API.del(apiName, path, requestInit));
  }

  /**
   * Regular HTTP request methods (for non-AWS APIs like letmc.com)
   */
  async request(config) {
    if (this.isDev) console.log('[API Service] HTTP request:', { url: config.url, method: config.method, platform: this.platform });
    
    // Use axios for web platform
    // Ensure timeout is set
    if (!config.timeout) {
      config.timeout = 30000; // 30 seconds
    }
    
    return await this.executeWithRetry(() => axiosInstance(config));
  }
}

// Create singleton instance
const apiService = new ApiService();

// Export individual methods bound to the instance
export const API_SERVICE = {
  // AWS Amplify API methods
  post: apiService.post.bind(apiService),
  get: apiService.get.bind(apiService),
  put: apiService.put.bind(apiService),
  patch: apiService.patch.bind(apiService),
  del: apiService.del.bind(apiService),

  // Regular HTTP methods
  request: apiService.request.bind(apiService)
};

// Export the service class for testing
export default ApiService;